import React, { useEffect, useState, useMemo } from "react";
import { Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  ApproveRejectUploadedExternalFileReq,
  ExternalFileRequestDto,
  GetAllExternalFileRequestsDto,
} from "../../../interfaces/ExternalFileReqeustDtos/externalFileRequestDtos";
import {
  ApproveRejectUploadedExternalFileAsync,
  downloadFileInNewTabAsync,
  GetAllExternalFileRequestAsync,
  GetUploadedExternalFileRequestsAsync,
  RevokeExternalFileRequestAsync,
} from "../../../services/ExternalFileRequestService/ExternalFileRequestService";
import { useAuth } from "../../../firebase/AuthProvider";
import { EXTERNAL_FILE_REQUEST_STATUS } from "../../../Data/SD";
import {
  showConfirmationDialog,
  showTextareaDialog,
} from "../../modal/ConfimationModal/Confimation";
import { toast } from "react-toastify";
import Loader from "../loader/Loader";
import { Download, Eye, Send, Trash2 } from "lucide-react";
import "../../../assets/css/drive.css";
import "../signature/signature.css";
import "./ExternalFileRequests.css";
import { convertUtcToLocalDate } from "../../../utilities/dateUtils";
import moment from "moment";
import { ROUTES } from "../../../Data/Routes/Routes";
import NoParticipantsFound from "../participantfiles/noParticipantsFound";
import { PaginationDto } from "../../../interfaces/Employee/Employee";

interface ExternalFileProps {
  activeTab: string;
  searchTerm: string;
  startDate: Date | null;
  endDate: Date | null;
  statusFilter?: string;
  pagination: PaginationDto;
  onTotalCountChange: (count: number) => void;
}

type SortState = {
  sortBy: string | null;
  direction: "asc" | "desc";
};

const ExternalFileRequests: React.FC<ExternalFileProps> = ({
  activeTab,
  searchTerm,
  startDate,
  endDate,
  statusFilter,
  pagination,
  onTotalCountChange,
}: ExternalFileProps) => {
  const { currentUser, currentOrganization } = useAuth();
  const navigate = useNavigate();
  const [allData, setAllData] = useState<ExternalFileRequestDto[]>([]);
  const [externalRequestCount, setExternalRequestCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [isReload, setIsReload] = useState(true);
  const [sort, setSort] = useState<SortState>({
    sortBy: null,
    direction: "desc",
  });
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  function formatDate(dateStr: string | null) {
    if (!dateStr) return "-";
    try {
      const localDate = convertUtcToLocalDate(dateStr);
      return localDate
        ? moment(localDate).format("MM/DD/YYYY, hh:mm:ss A")
        : "-";
    } catch (error) {
      console.error("Error formatting date:", dateStr, error);
      return "-";
    }
  }

  async function GetAllExternalFileRequest() {
    setLoading(true);
    try {
      if (!currentUser) return;
      const authToken = await currentUser.getIdToken();

      let result;

      if (activeTab === "inbox") {
        // For inbox tab, use the uploaded endpoint
        result = await GetUploadedExternalFileRequestsAsync(
          authToken,
          pagination.pageNumber,
          pagination.pageSize,
          debouncedSearchTerm || undefined,
          sort.sortBy || undefined,
          sort.direction || undefined
        );
      } else {
        // For sent tab, use the regular endpoint with all statuses
        // Map display value "Approved" to backend value "Accepted"
        const mapStatusForBackend = (status: string | undefined): string | null => {
          if (!status || status === "All Status") return null;
          if (status === "Approved") return "Accepted";
          return status;
        };

        const payload: GetAllExternalFileRequestsDto = {
          requesterId: currentUser?.uid,
          taskId: null,
          status: mapStatusForBackend(statusFilter),
          pageSize: pagination.pageSize,
          pageNumber: pagination.pageNumber,
          fromDate: startDate ? startDate.toISOString() : null,
          toDate: endDate ? endDate.toISOString() : null,
          sortBy: sort.sortBy,
          sortDirection: sort.direction,
          searchTerm: debouncedSearchTerm || null,
        };
        result = await GetAllExternalFileRequestAsync(authToken, payload);
      }

      if (result.success) {
        const data = result.data;
        setAllData(data.items);
        setExternalRequestCount(data.totalCount || data.items.length);
        
        // Update total count for pagination
        onTotalCountChange(data.totalCount || data.items.length);
      } else {
        setAllData([]);
      }
    } catch (err) {
      console.error("Error loading external file requests", err);
      setAllData([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    GetAllExternalFileRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReload, sort, activeTab, pagination.pageNumber, pagination.pageSize, debouncedSearchTerm, statusFilter, startDate, endDate]);

  // Filter data by date range
  const filterDataByDateRange = (
    data: ExternalFileRequestDto[]
  ): ExternalFileRequestDto[] => {
    if (!startDate && !endDate) return data;

    return data.filter((item) => {
      if (!item.createdAt) return false;

      try {
        const createdDate = new Date(item.createdAt);
        createdDate.setHours(0, 0, 0, 0);

        if (startDate && endDate) {
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          return createdDate >= start && createdDate <= end;
        } else if (startDate) {
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          return createdDate >= start;
        } else if (endDate) {
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          return createdDate <= end;
        }
      } catch (error) {
        console.error("Error parsing date:", item.createdAt);
        return false;
      }

      return true;
    });
  };

  // Filter data by date range (client-side filtering)
  // Note: Status and search term filtering are now handled by backend for both tabs
  // Inbox tab only shows uploaded status from backend
  const filteredRequests = useMemo(() => {
    let filtered = [...allData];

    // Filter by date range only (search and status are handled by backend)
    filtered = filterDataByDateRange(filtered);

    return filtered;
  }, [
    allData,
    activeTab,
    debouncedSearchTerm,
    startDate,
    endDate,
  ]);

  // Use filtered data directly (pagination is handled by backend)
  const paginatedData = filteredRequests;

  async function revokeRequest(externalFileRequestId: string) {
    if (!currentUser) return;
    const isConfirm = await showConfirmationDialog();
    if (!isConfirm) return;

    const token = await currentUser.getIdToken();
    setPageLoading(true);
    RevokeExternalFileRequestAsync(token, externalFileRequestId)
      .then((res) => {
        if (res.success) {
          setIsReload(!isReload);
          toast.success(res.message);
        } else {
          toast.error(res.message);
        }
      })
      .catch((err) => {
        toast.error(err?.message || "Error to Revoking");
      })
      .finally(() => {
        setPageLoading(false);
      });
  }

  function openTask(friendlyId: number | null) {
    if (!friendlyId || !currentOrganization) return;
    navigate(ROUTES.TASK_DETAILS(currentOrganization?.abbreviation, friendlyId));
  }

  function renderStatusBadge(status: string) {
    const s = status ? status : "";
    const base = " status-badge-css";
    switch (s) {
      case EXTERNAL_FILE_REQUEST_STATUS.PENDING:
        return (
          <span className={`${base} bg-[#FF890433] text-[#FF8904]`}>
            {status}
          </span>
        );
      case EXTERNAL_FILE_REQUEST_STATUS.UPLOADED:
      case EXTERNAL_FILE_REQUEST_STATUS.ACCEPTED:
      case EXTERNAL_FILE_REQUEST_STATUS.APPROVED:
        return (
          <span className={`${base} bg-[#00A63E1A] text-[#00A63E]`}>
            {status}
          </span>
        );
      case EXTERNAL_FILE_REQUEST_STATUS.REVOKED:
      case EXTERNAL_FILE_REQUEST_STATUS.REJECTED:
      case EXTERNAL_FILE_REQUEST_STATUS.EXPIRED:
        return (
          <span className={`${base} bg-[#FFE2E2] text-[#C10007]`}>
            {status}
          </span>
        );
      default:
        return (
          <span className={`${base} bg-gray-100 text-gray-800`}>{status}</span>
        );
    }
  }


  // Sorting handler function
  function handleSort(column: string) {
    setSort((prev) => {
      if (prev.sortBy === column) {
        // Toggle direction
        return {
          sortBy: column,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { sortBy: column, direction: "desc" }; // Default to desc
    });
  }

  // Helper for icon
  function getSortIcon(column: string) {
    if (sort.sortBy !== column) return "⇅";
    return sort.direction === "asc" ? "↑" : "↓";
  }

  const ApproveRejectUploadedExternalFile = async (
    externalFileRequestId: string,
    isAccept: boolean
  ) => {
    const token = await currentUser?.getIdToken();
    if (!token) return;

    let reasontext = "";
    if (isAccept) {
      const isConfirm = await showConfirmationDialog(
        "Confirm acceptance?",
        "This means you have reviewed the file and requested, you ensured this is a valid file with no corruption/issues and will be added to the task",
        "Yes, do it!",
        "Cancel",
        "warning"
      );
      if (!isConfirm) return;
    } else {
      const reason = await showTextareaDialog("Reason for Rejection", "");
      if (reason === null) return;
      reasontext = reason;
      const isConfirm = await showConfirmationDialog();
      if (!isConfirm) return;
    }

    const payload: ApproveRejectUploadedExternalFileReq = {
      externalFileRequestId: externalFileRequestId,
      isAccept: isAccept,
      reason: reasontext,
    };

    try {
      setPageLoading(true);
      const res = await ApproveRejectUploadedExternalFileAsync(token, payload);
      if (res.success) {
        toast.success(res.message);
        setIsReload(!isReload);
      } else {
        toast.error(res.message);
      }
    } catch (error: any) {
      toast.error(error.message || "Error to Accept/Reject the Request");
    } finally {
      setPageLoading(false);
    }
  };

  const downloadFileInNewTab = async (location: string) => {
    const token = await currentUser?.getIdToken();
    if (!token) return;

    try {
      setPageLoading(true);
      await downloadFileInNewTabAsync(token, location);
    } catch (error) {
      console.log("Error : ", error);
    } finally {
      setPageLoading(false);
    }
  };

  const handleSendRequest = async (externalFileRequestId: string) => {
    // TODO: Implement resend external file request functionality when API is available
    toast.info("Send functionality is not yet implemented");
  };

  const handleViewFile = (caseFileId: string | null) => {
    if (!caseFileId) {
      toast.error("File ID is missing. Cannot view file.");
      return;
    }
    navigate(`/casefile/file/${caseFileId}`);
  };

  const handleDownloadFile = async (location: string | null, fileName: string) => {
    if (!location) {
      toast.error("File location is missing. Cannot download file.");
      return;
    }
    await downloadFileInNewTab(location);
  };

  console.log("Files : ",allData );
  

  return (
    <div>
      {pageLoading && <Loader />}
      <div className="">
        <div className="signature-table-container">
          {!loading && paginatedData.length === 0 ? (
            <div className="no-participants-found-container">
              <NoParticipantsFound />
            </div>) : (
            <table className="drive-table min-w-[1300px] table" style={{ tableLayout: 'fixed' }}>
              <thead className="table-header-css">
                <tr>
                  <th
                    style={{ cursor: "pointer" }}
                    onClick={() => handleSort("requesteddisplayname")}
                  >
                    FILE NAME {getSortIcon("requesteddisplayname")}
                  </th>
                  <th
                    style={{ cursor: "pointer" }}
                    onClick={() => handleSort("recipientemail")}
                  >
                    RECIPIENT {getSortIcon("recipientemail")}
                  </th>
                  <th>STATUS</th>
                  <th>TASK</th>
                  <th
                    style={{ cursor: "pointer" }}
                    onClick={() => handleSort("createdat")}
                  >
                    CREATED DATE {getSortIcon("createdat")}
                  </th>
                  {activeTab == "sent" && (
                    <th
                      style={{ cursor: "pointer" }}
                      onClick={() => handleSort("tokenexpiresat")}
                    >
                      EXPIRES AT {getSortIcon("tokenexpiresat")}
                    </th>
                  )}

                  {/* )} */}
                  <th>UPLOADED AT</th>
                  <th>ACTIONS</th>
                  {/* {activeTab == "inbox" && (
                    <th ></th>
                  )} */}
                </tr>
              </thead>
              <tbody
                className={paginatedData.length >= 10 && pagination.pageSize === 10 ? "external-file-request-page-size-10" : ""}
              >
                {loading && (
                  <tr>
                    <td colSpan={8} className="text-center">
                      <Spinner animation="border" /> Loading...
                    </td>
                  </tr>
                )}

                {!loading &&
                  paginatedData.map((req) => (
                    <tr
                      key={req.externalFileRequestId}
                      className={paginatedData.length >= 10 && pagination.pageSize === 10 ? "row-height-10" : "row-height-10"}
                    >
                      <td className="shortDescription" title={req.requestedDisplayName}>{req.requestedDisplayName}</td>
                      <td className="shortDescription" title={req.recipientEmail}>{req.recipientEmail}</td>
                      <td>{renderStatusBadge(req.status)}</td>
                      <td>
                        <span
                          style={{
                            cursor: req.friendlyId ? "pointer" : "default",
                            // color: req.taskId ? "blue" : "inherit",
                            // textDecoration: req.taskId ? "underline" : "none",
                          }}
                           onClick={() => req.friendlyId && openTask(req.friendlyId)}
                        >
                          {req.friendlyId ? `${currentOrganization?.abbreviation}-${req.friendlyId}` : "N/A"}
                        </span>
                      </td>
                      <td className="shortDescription" title={formatDate(req.createdAt)}>{formatDate(req.createdAt)}</td>

                      {activeTab === "sent" && (
                        <td className="shortDescription" title={formatDate(req.tokenExpiresAt)}>{formatDate(req.tokenExpiresAt)}</td>
                      )}

                      <td className="shortDescription" title={req.uploadedAt ? formatDate(req.uploadedAt) : "--"}>
                        {req.uploadedAt ? (
                          <span>{formatDate(req.uploadedAt)}</span>
                        ) : (
                          "--"
                        )}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {activeTab === "sent" && req.status.includes(
                          EXTERNAL_FILE_REQUEST_STATUS.PENDING
                        ) ? (
                          <div className="action_btn">
                            <span>
                              <Send
                                onClick={() => handleSendRequest(req.externalFileRequestId)}
                              />
                            </span>
                            <span>
                              <Trash2
                                onClick={() => revokeRequest(req.externalFileRequestId)}
                              />
                            </span>
                          </div>
                        ) : activeTab === "sent" &&
                          req.status === EXTERNAL_FILE_REQUEST_STATUS.ACCEPTED ? (
                          <div className="action_btn">
                            <span>
                              <Eye
                                onClick={() => handleViewFile(req.caseFileId)}
                              />
                            </span>
                            <span>
                              <Download
                                onClick={() => handleDownloadFile(req.fileLocation, req.requestedDisplayName)}
                              />
                            </span>
                          </div>
                        ) : activeTab === "inbox" ? (
                          <div className="d-flex gap-[0.5rem]">
                            <span
                              onClick={() =>
                                ApproveRejectUploadedExternalFile(
                                  req?.id || "",
                                  false
                                )
                              }
                            >
                              <span className="rejected-btn">Reject</span>
                            </span>
                            <span
                              onClick={() =>
                                ApproveRejectUploadedExternalFile(
                                  req?.id || "",
                                  true
                                )
                              }
                            >
                              <span className="accept-btn">Accept</span>
                            </span>

                            <span
                              onClick={() => {
                                if (req.fileLocation) {
                                  downloadFileInNewTab(req.fileLocation);
                                }
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="30"
                                height="30"
                                viewBox="0 0 30 30"
                                fill="none"
                                style={{
                                  width: "1.875rem",
                                  height: "1.875rem",
                                  flexShrink: 0,
                                }}
                              >
                                <rect x="0.5" y="0.5" width="29" height="29" rx="4.5" stroke="#B7B7B7" />
                                <path d="M14 13.25C13.5359 13.25 13.0908 13.4344 12.7626 13.7626C12.4344 14.0908 12.25 14.5359 12.25 15C12.25 15.4641 12.4344 15.9092 12.7626 16.2374C13.0908 16.5656 13.5359 16.75 14 16.75C14.4641 16.75 14.9092 16.5656 15.2374 16.2374C15.5656 15.9092 15.75 15.4641 15.75 15C15.75 14.5359 15.5656 14.0908 15.2374 13.7626C14.9092 13.4344 14.4641 13.25 14 13.25ZM14 15.7C13.8143 15.7 13.6363 15.6262 13.505 15.495C13.3737 15.3637 13.3 15.1857 13.3 15C13.3 14.8143 13.3737 14.6363 13.505 14.505C13.6363 14.3737 13.8143 14.3 14 14.3C14.1857 14.3 14.3637 14.3737 14.495 14.505C14.6262 14.6363 14.7 14.8143 14.7 15C14.7 15.1857 14.6262 15.3637 14.495 15.495C14.3637 15.6262 14.1857 15.7 14 15.7ZM14 8C13.0807 8 12.1705 8.18106 11.3212 8.53284C10.4719 8.88463 9.70026 9.40024 9.05025 10.0503C7.7375 11.363 7 13.1435 7 15C7 16.8565 7.7375 18.637 9.05025 19.9497C9.70026 20.5998 10.4719 21.1154 11.3212 21.4672C12.1705 21.8189 13.0807 22 14 22C15.8565 22 17.637 21.2625 18.9497 19.9497C20.2625 18.637 21 16.8565 21 15C21 14.0807 20.8189 13.1705 20.4672 12.3212C20.1154 11.4719 19.5998 10.7003 18.9497 10.0503C18.2997 9.40024 17.5281 8.88463 16.6788 8.53284C15.8295 8.18106 14.9193 8 14 8ZM14 17.8C12.159 17.8 10.5 16.701 9.8 15C10.2649 13.8867 11.1526 13.0034 12.2681 12.5441C13.3837 12.0847 14.636 12.0868 15.75 12.55C16.857 13.0129 17.7371 13.893 18.2 15C17.5 16.701 15.841 17.8 14 17.8ZM14 13.25C13.5359 13.25 13.0908 13.4344 12.7626 13.7626C12.4344 14.0908 12.25 14.5359 12.25 15C12.25 15.4641 12.4344 15.9092 12.7626 16.2374C13.0908 16.5656 13.5359 16.75 14 16.75C14.4641 16.75 14.9092 16.5656 15.2374 16.2374C15.5656 15.9092 15.75 15.4641 15.75 15C15.75 14.5359 15.5656 14.0908 15.2374 13.7626C14.9092 13.4344 14.4641 13.25 14 13.25ZM14 15.7C13.8143 15.7 13.6363 15.6262 13.505 15.495C13.3737 15.3637 13.3 15.1857 13.3 15C13.3 14.8143 13.3737 14.6363 13.505 14.505C13.6363 14.3737 13.8143 14.3 14 14.3C14.1857 14.3 14.3637 14.3737 14.495 14.505C14.6262 14.6363 14.7 14.8143 14.7 15C14.7 15.1857 14.6262 15.3637 14.495 15.495C14.3637 15.6262 14.1857 15.7 14 15.7Z" fill="#B7B7B7" />
                              </svg>
                            </span>
                          </div>
                        ) : (
                          <>--</>
                        )}
                      </td>
                      {/* {activeTab === "inbox" && (
                        <td className="w-[100px]"></td>
                      )} */}
                    </tr>
                  ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExternalFileRequests;
